import cx from 'classnames';
import { isEmpty, map } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { ordersApi } from 'api';
import { Modal, Spinner } from 'components';
import { QueryKey } from 'enums';
import { TranslationNamespace } from 'i18n';

type Props = {
  open: boolean;
  orderId: string;
  currency: string;
  alternativeAmountEnabled?: boolean;
  alternativeTradeMethodEnabled?: boolean;
  onSelect: (amount: number) => void;
  onSelectTradeMethod: () => void;
  onDismiss: () => void;
};

export const SqAlternativeOrderAmountSelectionModal: React.FC<Props> = ({
  open,
  orderId,
  currency,
  alternativeAmountEnabled,
  alternativeTradeMethodEnabled,
  onSelect,
  onSelectTradeMethod,
  onDismiss,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_customer_alternatives_modal',
  });

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  useEffect(() => {
    if (!open) {
      setSelectedIndex(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (open && !alternativeAmountEnabled && !alternativeTradeMethodEnabled) {
      onDismiss();
    }
  }, [
    open,
    alternativeAmountEnabled,
    alternativeTradeMethodEnabled,
    onDismiss,
  ]);

  const handleSuccess = useCallback(
    (amounts: number[]) => {
      if (isEmpty(amounts)) {
        if (!alternativeTradeMethodEnabled) {
          onDismiss();
        }
        return;
      }

      setSelectedIndex(0);
    },
    [onDismiss, alternativeTradeMethodEnabled],
  );

  const { data: amounts = [], isLoading } = useQuery(
    [QueryKey.AlternativeAmounts, orderId],
    () => ordersApi.offerAlternativeAmounts(orderId),
    {
      enabled: open && !!alternativeAmountEnabled,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: handleSuccess,
    },
  );

  return (
    <Modal open={open} mobileViewOnly onClose={onDismiss} title={t('title')}>
      {isLoading ? (
        <div className="tw-flex tw-justify-center tw-py-6">
          <Spinner />
        </div>
      ) : (
        <>
          {alternativeAmountEnabled && !isLoading && !isEmpty(amounts) && (
            <>
              <p className="tw-text-sm tw-text-secondary tw-mb-6">
                {t('description')}
              </p>
              <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-6">
                {map(amounts, (amount, index) => {
                  const isSelected = index === selectedIndex;
                  return (
                    <button
                      key={amount}
                      className={cx(
                        'tw-h-[54px] tw-rounded-[16px] tw-text-primary tw-font-medium tw-text-sm tw-cursor-pointer',
                        'tw-flex tw-items-center tw-justify-center tw-gap-1.5',
                        'tw-transition-colors tw-duration-150',
                        isSelected
                          ? 'tw-bg-payment-layout tw-border-2 tw-border-blue-500'
                          : 'tw-bg-payment-layout tw-border-2 tw-border-transparent',
                      )}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <span className="tw-text-base tw-font-semibold">{`${amount} ${currency}`}</span>
                      {isSelected && (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <circle cx="9" cy="9" r="9" fill="#2563EB" />
                          <path
                            d="M5 9l3 3 5-5"
                            stroke="white"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                className="tw-w-full tw-h-[54px] tw-rounded-[16px] tw-bg-blue-500 tw-text-white tw-font-semibold tw-text-base tw-border-none tw-cursor-pointer tw-transition-opacity tw-duration-150 hover:tw-opacity-90 disabled:tw-opacity-50 tw-mb-3"
                disabled={selectedIndex === undefined}
                onClick={() =>
                  selectedIndex !== undefined &&
                  onSelect(amounts[selectedIndex])
                }
              >
                {t('select_button')}
              </button>
            </>
          )}

          {alternativeTradeMethodEnabled && (
            <>
              <p className="tw-text-sm tw-text-secondary tw-mb-6">
                {t('description_trade_method')}
              </p>
              <button
                className="tw-w-full tw-h-[54px] tw-rounded-[16px] tw-bg-payment-layout tw-text-primary tw-font-semibold tw-text-base tw-border-2 tw-border-blue-500 tw-cursor-pointer tw-transition-opacity tw-duration-150 hover:tw-opacity-90"
                onClick={onSelectTradeMethod}
              >
                {t('select_trade_method_button')}
              </button>
            </>
          )}
        </>
      )}
    </Modal>
  );
};
